import React, { lazy, Suspense } from 'react'
import ErrorBoundary from '@fs/zion-error-boundary'
import { Switch, Route, NotFound } from '@fs/zion-router'
import { pidRegexStr } from '@fs/zion-pid'
import { useFeatureFlag } from '@fs/zion-flags'
import { DataContext } from './services/data-service'
import { DefaultScriptsContext } from './services/default-scripts-service'
import { SurnameIndexContext } from './services/surname-index-service'
import { getExternalBaseUrl } from './services/url-service'
import { appSupportedLocales } from './types/appSupportedLocales'
import LoadingPage from './components/AncestorPage/LoadingPage'

const DevelopmentPage = lazy(() => import('./DevelopmentPage'))
const AncestorPage = lazy(() => import('./components/AncestorPage/AncestorPage'))
const HomePage = lazy(() => import('./components/HomePage/HomePage'))
const SearchPage = lazy(() => import('./components/search/SearchPage'))
const IndexPage = lazy(() => import('./components/surnames/IndexPage'))
const ScriptsPage = lazy(() => import('./components/surnames/ScriptsPage'))
const SavedHtmlError = lazy(() => import('./components/errors/SavedHtmlError'))
const SCRIPT_MATCH = ':script([a-z]{4})'
const PID_MATCH = `:pid(${pidRegexStr})`
const SURNAMES_MATCH = ':surnames(surnames)'
const LOCALE_MATCH = `:locale(${appSupportedLocales.join('|')})`
const CACHE_BUST_MATCH = ':cb(cb[0-9]{13})'

function DomainRedirect(): null {
  window.location.href = `${getExternalBaseUrl()}${window.location.pathname}`
  return null
}
// TODO when do we actually pass pid and initialData? Do we still need these props? Nothing uses initialData.
type Props = { pid?: string; initialData?: unknown }

// TODO the /tree/ancestors paths are not necessary due to the appPathOverride. However, the frontier_dtmulator still goes to /tree/ancestors for now.
const OLD_DEFAULT_PATHS = [
  '/',
  `/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}`,
  `/${LOCALE_MATCH}/${CACHE_BUST_MATCH}`,
  '/tree/ancestors/',
  `/tree/ancestors/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${CACHE_BUST_MATCH}`,
]

const NEW_DEFAULT_PATHS = ['/', `/${CACHE_BUST_MATCH}`, '/tree/ancestors/', `/tree/ancestors/${CACHE_BUST_MATCH}`]

const OLD_SEARCH_PATHS = [
  '/search',
  `/search/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/search`,
  `/${LOCALE_MATCH}/search/${CACHE_BUST_MATCH}`,
  '/tree/ancestors/search',
  `/tree/ancestors/search/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/search`,
  `/tree/ancestors/${LOCALE_MATCH}/search/${CACHE_BUST_MATCH}`,
]

const NEW_SEARCH_PATHS = [
  '/search',
  `/search/${CACHE_BUST_MATCH}`,
  '/tree/ancestors/search',
  `/tree/ancestors/search/${CACHE_BUST_MATCH}`,
]

const OLD_SURNAMES_DEFAULT_PATHS = [
  `/${SURNAMES_MATCH}`,
  `/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
]

const NEW_SURNAMES_DEFAULT_PATHS = [
  `/${SURNAMES_MATCH}`,
  `/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
]

const OLD_SURNAMES_SCRIPT_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
]

const NEW_SURNAMES_SCRIPT_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
]

const OLD_SURNAMES_SCRIPT_ETC_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/tree/ancestors/${LOCALE_MATCH}/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
]

const NEW_SURNAMES_SCRIPT_ETC_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
]

const OLD_PID_PATHS = [
  `/${PID_MATCH}`,
  `/${PID_MATCH}/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${PID_MATCH}`,
  `/${LOCALE_MATCH}/${PID_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}/${CACHE_BUST_MATCH}`,
]

const NEW_PID_PATHS = [
  `/${PID_MATCH}`,
  `/${PID_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/${CACHE_BUST_MATCH}`,
]

export const OLD_ANCESTOR_PAGE_PATHS = [
  `/${PID_MATCH}/:nameAndLifespan`,
  `/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan`,
  `/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
]

export const NEW_ANCESTOR_PAGE_PATHS = [
  `/${PID_MATCH}/:nameAndLifespan`,
  `/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
]

export default function App(props: Props): JSX.Element {
  const isLangCodeFromFrontierOn = useFeatureFlag('shared_addLangCodeToUrl').isOn

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingPage />}>
        <Switch>
          {process.env.NODE_ENV !== 'production' && <Route exact path="/dev" component={DevelopmentPage} />}

          <Route exact path={isLangCodeFromFrontierOn ? NEW_DEFAULT_PATHS : OLD_DEFAULT_PATHS}>
            <DefaultScriptsContext {...props}>
              <HomePage />
            </DefaultScriptsContext>
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_SEARCH_PATHS : OLD_SEARCH_PATHS}>
            <SearchPage />
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_SURNAMES_DEFAULT_PATHS : OLD_SURNAMES_DEFAULT_PATHS}>
            <SurnameIndexContext {...props}>
              <ScriptsPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_SURNAMES_SCRIPT_PATHS : OLD_SURNAMES_SCRIPT_PATHS}>
            <SurnameIndexContext {...props}>
              <IndexPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_SURNAMES_SCRIPT_ETC_PATHS : OLD_SURNAMES_SCRIPT_ETC_PATHS}>
            <SurnameIndexContext {...props}>
              <IndexPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_PID_PATHS : OLD_PID_PATHS}>
            <DataContext {...props}>
              <AncestorPage />
            </DataContext>
          </Route>

          <Route exact path={isLangCodeFromFrontierOn ? NEW_ANCESTOR_PAGE_PATHS : OLD_ANCESTOR_PAGE_PATHS}>
            <DataContext {...props}>
              <AncestorPage />
            </DataContext>
          </Route>
          <Route
            path={[
              '*/Desktop/*',
              '*/Users/*',
              '*/USUARIOS/*',
              '*/OneDrive/*',
              '*/Museum/*',
              '*/Personal/*',
              '*/Pictures/*',
            ]}
          >
            <DataContext {...props}>
              <SavedHtmlError />
            </DataContext>
          </Route>

          <Route path={['/tree/person/*', '/tree/pedigree/*']}>
            <DomainRedirect />
          </Route>

          <Route path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
